import { useState, ReactNode, useEffect } from 'react';
import { CheckCircle, Info, WarningCircle, XCircle } from '@phosphor-icons/react';

type AlertType = 'info' | 'warning' | 'danger' | 'success';
type AlertPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';

interface ShowAlertOptions {
    type: AlertType;
    message: string;
    position: AlertPosition;
    icon?: ReactNode;
    time?: number;
}

export const useAlert = () => {
    const [alert, setAlert] = useState<ShowAlertOptions | null>(null);
    const [visible, setVisible] = useState(false);

    const showAlert = (options: ShowAlertOptions) => {
        setAlert(options);
        setVisible(true);

        setTimeout(() => {
            setVisible(false);
            setTimeout(() => setAlert(null), 500); // Delay to allow fade-out animation to complete
        }, (options.time || 3) * 1000);
    };

    const AlertComponent = () => {
        if (!alert) return null;

        const { type, message, position, icon } = alert;

        // Determine styles based on type
        const typeStyles = {
            info: { background: 'bg-blue-100', border: 'border-blue-500', iconColor: 'text-blue-500', icon: icon || <Info /> },
            warning: { background: 'bg-yellow-100', border: 'border-yellow-500', iconColor: 'text-yellow-500', icon: icon || <WarningCircle /> },
            danger: { background: 'bg-red-100', border: 'border-red-500', iconColor: 'text-red-500', icon: icon || <XCircle /> },
            success: { background: 'bg-green-100', border: 'border-green-500', iconColor: 'text-green-500', icon: icon || <CheckCircle /> },
        };

        const { background, border, iconColor, icon: IconComponent } = typeStyles[type];

        const positionStyles = {
            'top-left': 'top-4 left-4',
            'top-right': 'top-4 right-4',
            'bottom-left': 'bottom-4 left-4',
            'bottom-right': 'bottom-4 right-4',
            'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
            'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
        };

        return (
            <div
                className={`fixed ${positionStyles[position]} p-4 rounded-lg shadow-lg ${background} ${border} border text-black flex items-center space-x-3 transition-opacity duration-500 ${
                    visible ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                    boxShadow: '4px 4px 8px #bebebe, -4px -4px 8px #ffffff',
                }}
            >
                {IconComponent && <div className={`w-5 h-5 ${iconColor}`}>{IconComponent}</div>}
                <span>{message}</span>
            </div>
        );
    };

    return { showAlert, AlertComponent };
};
